import React from "react";
import { NavLink, Outlet, Navigate, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  ScrollText,
  Trash2,
  Activity,
  ReceiptText,
  TicketPercent,
  Sparkles,
  ArrowLeft,
  LogOut,
  Cloud,
  Palette,
  Server,
} from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { EmieWidget } from "@/components/EmieWidget";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/audit", label: "Audit Logs", icon: ScrollText },
  { to: "/admin/deleted", label: "Recently Deleted", icon: Trash2 },
  { to: "/admin/billing", label: "Billing Pulse", icon: Activity },
  { to: "/admin/invoices", label: "Invoices & GST", icon: ReceiptText },
  { to: "/admin/promos", label: "Promo Codes", icon: TicketPercent },
  { to: "/admin/emie", label: "EMIE Quality", icon: Sparkles },
  { to: "/admin/salesforce", label: "Salesforce App", icon: Cloud },
  { to: "/admin/instance", label: "White-label", icon: Palette },
  { to: "/admin/deployments", label: "Deployments", icon: Server },
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();

  if (user === null) return null;
  if (!user || user.role !== "superadmin") return <Navigate to="/dashboard" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-card/40 md:flex">
        <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
          <LogoMark size={32} />
          <div className="leading-tight">
            <p className="font-display text-sm font-bold">{branding.product_short || "GovernX"}</p>
            <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-accent">Super Admin</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`
                }
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <Icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-border p-3">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-muted-foreground">
              <ArrowLeft className="h-4 w-4" /> Back to app
            </Button>
          </Link>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-border bg-background/80 px-6 py-3 backdrop-blur">
          <div className="flex items-center gap-3 md:hidden">
            <LogoMark size={28} />
            <span className="font-display text-sm font-bold">Super Admin</span>
          </div>
          <div className="hidden md:block" />
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-muted-foreground sm:inline" data-testid="admin-email">
              {user.email}
            </span>
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={handleLogout} className="gap-2" data-testid="admin-logout">
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Log out</span>
            </Button>
          </div>
        </header>

        {/* Mobile nav */}
        <nav className="flex items-center gap-1 overflow-x-auto border-b border-border px-3 py-2 md:hidden">
          <Link to="/dashboard" className="mr-1 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold text-primary">
            Back to app
          </Link>
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium ${
                  isActive ? "bg-primary/10 text-primary" : "text-muted-foreground"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      <EmieWidget />
    </div>
  );
}
